import { useState, useEffect, useMemo, Fragment } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useGeri } from '@/lib/geri';
import api from '@/lib/api';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ArrowLeft, ChefHat, X, Pencil, Trash2, ArrowUpDown, ArrowUp, ArrowDown, TrendingUp, DollarSign, Package, ChevronRight, ChevronDown as ChevronAcik, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { trSayi, trPara } from '@/lib/sayi';
import ReactMarkdown from 'react-markdown';

const typeLabels = {
  raw_material: 'Hammadde',
  semi_finished: 'Yari Mamul',
  finished: 'Mamul',
  packaging: 'Ambalaj',
  other: 'Diger'
};

export default function RecipeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  // Geri = önceki sayfa; önceki adım yoksa eski liste adresi (bkz. lib/geri.js)
  const geri = useGeri('/erp/recipes');
  const [recipe, setRecipe] = useState(null);
  usePageTitle(recipe?.name || 'Reçete Detayı');
  const [cost, setCost] = useState(null);
  const [salesStats, setSalesStats] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [productImageLightbox, setProductImageLightbox] = useState(false);
  const [costSort, setCostSort] = useState({ key: null, dir: 'asc' });
  const [versiyonlar, setVersiyonlar] = useState([]);

  useEffect(() => {
    api.get(`/recipes/${id}`).then(r => setRecipe(r.data)).catch(() => toast.error('Recete yuklenemedi'));
    api.get(`/recipes/${id}/cost`).then(r => setCost(r.data)).catch(() => {});
    // Fetch sales stats for this recipe
    api.get(`/recipes/${id}/sales-stats`).then(r => setSalesStats(r.data)).catch(() => {});
    api.get(`/recipes/${id}/versions`).then(r => setVersiyonlar(r.data.versions || [])).catch(() => {});
  }, [id]);

  // Bir versiyonu hesaplamalarda kullanilan versiyon yapar
  const versiyonAktifYap = async (vid, no, mevcutTarih) => {
    const tarih = window.prompt(
      `v${no} hangi tarihten itibaren geçerli olsun?\n\n`
      + `Bugünü yazarsanız hemen geçerli olur. İleri bir tarih yazarsanız o güne `
      + `kadar önceki versiyon kullanılmaya devam eder. Geçmiş bir tarih yazarsanız `
      + `geçmiş kaydı düzeltilmiş olur.\n\nBiçim: YYYY-AA-GG`,
      mevcutTarih || new Date().toISOString().slice(0, 10));
    if (!tarih) return;
    try {
      const res = await api.patch(`/recipes/${vid}/activate`, { valid_from: tarih.trim() });
      const gecerli = res.data?.su_an_gecerli_versiyon;
      toast.success(gecerli === no
        ? `v${no} artık geçerli versiyon`
        : `v${no} ${tarih} tarihinden geçerli — bugün v${gecerli} kullanılıyor`);
      const [v, r] = await Promise.all([
        api.get(`/recipes/${id}/versions`),
        api.get(`/recipes/${id}`),
      ]);
      setVersiyonlar(v.data.versions || []);
      setRecipe(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
  };

  // Calculate unit totals for ingredients
  const unitTotals = useMemo(() => {
    if (!recipe?.ingredients) return {};
    const totals = {};
    recipe.ingredients.forEach(ing => {
      const unit = ing.unit || 'adet';
      if (!totals[unit]) totals[unit] = 0;
      totals[unit] += parseFloat(ing.quantity) || 0;
    });
    return totals;
  }, [recipe]);

  // Sorted cost ingredients
  const sortedCostIngredients = useMemo(() => {
    if (!cost?.ingredients) return [];
    const items = [...cost.ingredients];
    if (costSort.key) {
      items.sort((a, b) => {
        let aVal = a[costSort.key];
        let bVal = b[costSort.key];
        if (typeof aVal === 'string') aVal = aVal.toLowerCase();
        if (typeof bVal === 'string') bVal = bVal.toLowerCase();
        if (aVal < bVal) return costSort.dir === 'asc' ? -1 : 1;
        if (aVal > bVal) return costSort.dir === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return items;
  }, [cost, costSort]);

  // Alt reçeteden gelen kalemler kendi başlığı altında toplanır.
  //
  // NEDEN: BOM tamamen düzleştiriliyordu; alt reçetenin malzemeleri ana
  // reçetenin kalemleriymiş gibi tek listede duruyordu. Kuzu Kol'da 23 kalemlik
  // reçete 29 satır gösteriyor, üstelik aynı malzeme (defne yaprağı) hem
  // doğrudan hem kompostonun içinden geldiği için iki kez çıkıyordu.
  //
  // SIRALAMA seçilince gruplama bırakılır: "en pahalı ne?" diye bakarken
  // hiyerarşi engel oluyor.
  const [acikGruplar, setAcikGruplar] = useState({});
  const grupCevir = (k) => setAcikGruplar(p => ({ ...p, [k]: !p[k] }));

  const costRows = useMemo(() => {
    if (costSort.key) return sortedCostIngredients.map(ing => ({ tip: 'kalem', ing }));
    const satirlar = [];
    const gruplar = new Map();
    (cost?.ingredients || []).forEach((ing, i) => {
      if (ing.kaynak) {
        const k = ing.kaynak.urun_id;
        if (!gruplar.has(k)) {
          const g = { ad: ing.kaynak.urun_adi, uretimTuru: ing.kaynak.uretim_turu,
                      kalemler: [], toplam: 0 };
          gruplar.set(k, g);
          satirlar.push({ tip: 'grup', anahtar: k, grup: g });
        }
        const g = gruplar.get(k);
        g.kalemler.push(ing);
        g.toplam += ing.total_cost || 0;
      } else {
        satirlar.push({ tip: 'kalem', ing, anahtar: `k${i}` });
      }
    });
    return satirlar;
  }, [cost, costSort, sortedCostIngredients]);

  const handleCostSort = (key) => {
    setCostSort(prev => ({
      key,
      dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortIcon = ({ column }) => {
    if (costSort.key !== column) return <ArrowUpDown size={12} className="ml-1 opacity-40" />;
    return costSort.dir === 'asc' ? <ArrowUp size={12} className="ml-1" /> : <ArrowDown size={12} className="ml-1" />;
  };

  if (!recipe) return <div className="text-center py-8 text-[#8a7e70]">Yukleniyor...</div>;

  const photos = recipe.photos || [];

  return (
    <div data-testid="recipe-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={geri}
            className="text-[#8a7e70] hover:text-[#1a1916] hover:bg-[#e8dfd0]">
            <ArrowLeft size={20} />
          </Button>
          <div className="flex items-center gap-3">
            {recipe?.product_image_url ? (
              <div 
                className="w-14 h-14 rounded-lg overflow-hidden cursor-pointer border-2 border-[#d0c4b0] hover:border-[#2e2d29] transition-colors shadow-md"
                onClick={() => setProductImageLightbox(true)}
                data-testid="product-image-thumbnail"
              >
                <img 
                  src={recipe.product_image_url} 
                  alt={recipe.product_name} 
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="w-12 h-12 rounded-lg bg-[#2e2d29] flex items-center justify-center">
                <ChefHat className="text-[#f5f0e8]" size={24} />
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold text-[#1a1916] tracking-tight">{recipe?.product_name || 'Recete Detay'}</h1>
              <p className="text-sm text-[#8a7e70]">{recipe?.product_code} • Versiyon {recipe.version || 1}</p>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Link to={`/erp/recipes/${id}/edit`}>
            <Button className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">
              <Pencil size={16} className="mr-2" /> Duzenle
            </Button>
          </Link>
        </div>
      </div>

      {/* Info Cards Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <InfoCard label="Verim Miktari" value={`${recipe.yield_quantity || 1} ${recipe.yield_unit || 'adet'}`} />
        <InfoCard label="Toplam Maliyet" value={`${trPara(cost?.total_cost)} TL`} />
        <InfoCard label="Birim Maliyet" value={`${trPara(cost?.unit_cost)} TL`} />
        <InfoCard
          label={`Durum (v${recipe.version ?? '?'})`}
          value={recipe.is_active ? 'Aktif' : 'Pasif'}
          className={recipe.is_active ? 'text-[#3d6e4e]' : 'text-[#7a3535]'}
        />
      </div>

      {/* Versiyonlar — bir urunun recetesi zamanla degisir; hesaplamalarda
          yalnizca AKTIF versiyon kullanilir. */}
      {versiyonlar.length > 0 && (
        <Card className="bg-[#faf7f2] border-[#d0c4b0] shadow-sm mb-6">
          <CardHeader className="border-b border-[#d0c4b0]">
            <CardTitle className="text-lg font-bold text-[#1a1916] flex items-center gap-2">
              <ArrowUpDown size={18} /> Reçete Versiyonları ({versiyonlar.length})
            </CardTitle>
            <p className="text-sm text-[#8a7e70] mt-1">
              Hangi versiyonun kullanıldığı <strong>geçerlilik tarihinden</strong> belirlenir:
              o güne kadarki en son başlayan versiyon geçerlidir. Yeni reçete yüklemek
              (elle ya da Excel) eskisini silmez, yeni versiyon açar.
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Versiyon</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-center">Durum</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Geçerlilik</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Üretim</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Malzeme</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Birim Maliyet</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Oluşturma</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Oluşturan</TableHead>
                  <TableHead className="text-right"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {versiyonlar.map(v => (
                  <TableRow key={v.id}
                    className={`border-[#d0c4b0] ${v.id === id ? 'bg-[#e8dfd0]/40' : ''}`}>
                    <TableCell className="font-semibold text-[#1a1916]">
                      {v.id === id ? `v${v.version}` : (
                        <Link to={`/erp/recipes/${v.id}`} className="hover:underline">v{v.version}</Link>
                      )}
                      {v.id === id && <span className="text-xs text-[#8a7e70] font-normal"> (görüntülenen)</span>}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={`status-badge ${v.su_an_gecerli
                        ? 'bg-[#3d6e4e]/10 text-[#3d6e4e]'
                        : v.ileri_tarihli ? 'bg-[#7a6a2e]/10 text-[#7a6a2e]'
                          : 'bg-[#e8dfd0] text-[#8a7e70]'}`}>
                        {v.su_an_gecerli ? 'Geçerli' : v.ileri_tarihli ? 'İleri tarihli' : 'Geçmiş'}
                      </span>
                    </TableCell>
                    {/* Gecerlilik donemi: bu versiyonun basladigi gunden, bir
                        sonraki versiyonun basladigi gune kadar */}
                    <TableCell className="text-sm text-[#3F3E39] whitespace-nowrap">
                      {v.valid_from}
                      <span className="text-[#8a7e70]">
                        {v.valid_to ? ` → ${v.valid_to}` : ' →'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right text-sm text-[#3F3E39]">
                      {v.yield_quantity} {v.yield_unit}
                    </TableCell>
                    <TableCell className="text-right text-sm text-[#8a7e70]">{v.ingredient_count} kalem</TableCell>
                    <TableCell className="text-right font-mono text-sm text-[#3F3E39]">
                      {v.unit_cost != null ? `${trPara(v.unit_cost)} TL` : '-'}
                    </TableCell>
                    <TableCell className="text-sm text-[#8a7e70]">
                      {v.created_at ? new Date(v.created_at).toLocaleDateString('tr-TR') : '-'}
                      {v.duzenlendi && (
                        <span className="block text-xs text-[#7a6a2e]">
                          düzenlendi: {new Date(v.updated_at).toLocaleDateString('tr-TR')}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-[#8a7e70]">{v.created_by_name || '-'}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost"
                        onClick={() => versiyonAktifYap(v.id, v.version, v.valid_from)}
                        className="text-[#3d6e4e] hover:bg-[#3d6e4e]/10">
                        {v.su_an_gecerli ? 'Tarihi düzelt' : 'Geçerli yap'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Sales Statistics */}
      {salesStats && (
        <Card className="bg-gradient-to-r from-[#2e2d29] to-[#3F3E39] border-none shadow-lg mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-bold text-[#f5f0e8] flex items-center gap-2">
              <TrendingUp size={20} /> Satis Istatistikleri
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-xs text-[#a09080] uppercase font-medium">Toplam Satis</p>
                <p className="text-xl font-bold text-[#f5f0e8]">{salesStats.total_quantity || 0} adet</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-xs text-[#a09080] uppercase font-medium">Satis Tutari</p>
                <p className="text-xl font-bold text-[#f5f0e8]">{trPara(salesStats.total_revenue)} TL</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-xs text-[#a09080] uppercase font-medium">Toplam Maliyet</p>
                <p className="text-xl font-bold text-[#f5f0e8]">{trPara(salesStats.total_cost)} TL</p>
              </div>
              <div className={`rounded-lg p-3 ${(salesStats.total_profit || 0) >= 0 ? 'bg-[#3d6e4e]/30' : 'bg-[#7a3535]/30'}`}>
                <p className="text-xs text-[#a09080] uppercase font-medium">Kar / Zarar</p>
                <p className={`text-xl font-bold ${(salesStats.total_profit || 0) >= 0 ? 'text-[#7cba72]' : 'text-[#e57373]'}`}>
                  {(salesStats.total_profit || 0) >= 0 ? '+' : ''}{trPara(salesStats.total_profit)} TL
                </p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-xs text-[#a09080] uppercase font-medium">Kar Marji</p>
                <p className={`text-xl font-bold ${(salesStats.profit_margin || 0) >= 0 ? 'text-[#7cba72]' : 'text-[#e57373]'}`}>
                  %{trSayi(salesStats.profit_margin, 1)}
                </p>
              </div>
            </div>
            <p className="text-xs text-[#8a7e70] mt-3 italic">
              * Kar/zarar satisın gerceklestigi gunki maliyete gore hesaplanmistir
            </p>
          </CardContent>
        </Card>
      )}

      {/* Preparation Instructions */}
      {recipe.preparation_steps && (
        <Card className="bg-[#faf7f2] border-[#d0c4b0] shadow-sm mb-6">
          <CardHeader className="border-b border-[#d0c4b0]">
            <CardTitle className="text-lg font-bold text-[#1a1916]">Yapim Tarifi</CardTitle>
          </CardHeader>
          <CardContent className="p-6 prose prose-sm max-w-none text-[#3F3E39]">
            <ReactMarkdown>{recipe.preparation_steps}</ReactMarkdown>
          </CardContent>
        </Card>
      )}

      {/* Important Notes */}
      {recipe.important_notes && (
        <Card className="bg-[#faf7f2] border-[#d0c4b0] shadow-sm mb-6">
          <CardHeader className="border-b border-[#d0c4b0]">
            <CardTitle className="text-lg font-bold text-[#1a1916]">Onemli Noktalar</CardTitle>
          </CardHeader>
          <CardContent className="p-6 prose prose-sm max-w-none text-[#3F3E39]">
            <ReactMarkdown>{recipe.important_notes}</ReactMarkdown>
          </CardContent>
        </Card>
      )}

      {/* Photos */}
      {photos.length > 0 && (
        <Card className="bg-[#faf7f2] border-[#d0c4b0] shadow-sm mb-6">
          <CardHeader className="border-b border-[#d0c4b0]">
            <CardTitle className="text-lg font-bold text-[#1a1916]">Fotograflar ({photos.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {photos.map((photo, i) => (
                <div key={photo.id || i} className="cursor-pointer group" onClick={() => setLightbox(photo)}>
                  <img src={photo.url} alt={photo.caption || ''} className="w-full h-32 object-cover rounded-lg border border-[#d0c4b0] group-hover:border-[#2e2d29] transition-colors" />
                  {photo.caption && <p className="text-xs text-[#8a7e70] mt-1 truncate">{photo.caption}</p>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ingredients - Notes column removed */}
      <Card className="bg-[#faf7f2] border-[#d0c4b0] shadow-sm mb-6">
        <CardHeader className="border-b border-[#d0c4b0]">
          <CardTitle className="text-lg font-bold text-[#1a1916]">Malzemeler</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
                <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Urun</TableHead>
                <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Kod</TableHead>
                <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Tur</TableHead>
                <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Miktar</TableHead>
                <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Birim</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recipe.ingredients?.map((ing, i) => (
                <TableRow key={i} className="border-[#d0c4b0]">
                  <TableCell className="font-medium text-[#1a1916]">{ing.product_name || '-'}</TableCell>
                  <TableCell className="font-mono text-sm text-[#8a7e70]">{ing.product_code || '-'}</TableCell>
                  <TableCell>
                    <span className="text-xs px-2 py-0.5 rounded bg-[#e8dfd0] text-[#3F3E39]">
                      {typeLabels[ing.product_type] || ing.product_type || '-'}
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-semibold text-[#1a1916]">{ing.quantity}</TableCell>
                  <TableCell className="text-[#8a7e70]">{ing.unit}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {/* Unit Totals */}
          {Object.keys(unitTotals).length > 0 && (
            <div className="p-4 bg-[#e8dfd0]/50 border-t border-[#d0c4b0]">
              <p className="text-xs font-bold uppercase tracking-wider text-[#8a7e70] mb-2">Birim Toplamlari</p>
              <div className="flex flex-wrap gap-3">
                {Object.entries(unitTotals).sort((a, b) => a[0].localeCompare(b[0])).map(([unit, total]) => (
                  <span key={unit} className="px-3 py-1.5 bg-white rounded border border-[#d0c4b0] text-sm">
                    <span className="font-semibold text-[#1a1916]">{trSayi(total)}</span>
                    <span className="text-[#8a7e70] ml-1">{unit}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* BOM Cost Breakdown */}
      {cost && cost.ingredients && (
        <Card className="bg-[#faf7f2] border-[#d0c4b0] shadow-sm mb-6">
          <CardHeader className="border-b border-[#d0c4b0]">
            <CardTitle className="text-lg font-bold text-[#1a1916]">Maliyet Dagilimi (BOM)</CardTitle>
            {costSort.key ? (
              <p className="text-xs text-[#8a7e70] mt-1">
                Sıralı görünüm — alt reçete gruplaması kapalı.{' '}
                <button type="button" onClick={() => setCostSort({ key: null, dir: 'asc' })}
                  className="text-[#3d6e4e] hover:underline" data-testid="bom-gruplamaya-don">
                  Gruplamaya dön
                </button>
              </p>
            ) : (
              <p className="text-xs text-[#8a7e70] mt-1">
                Alt reçeteler kendi başlığı altında; açmak için satıra tıklayın.
              </p>
            )}
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
                  <TableHead 
                    className="text-[#1a1916] font-bold text-xs uppercase cursor-pointer hover:bg-[#d0c4b0]/50 select-none"
                    onClick={() => handleCostSort('product_name')}
                  >
                    <span className="flex items-center">Hammadde <SortIcon column="product_name" /></span>
                  </TableHead>
                  <TableHead 
                    className="text-[#1a1916] font-bold text-xs uppercase cursor-pointer hover:bg-[#d0c4b0]/50 select-none"
                    onClick={() => handleCostSort('product_type')}
                  >
                    <span className="flex items-center">Tur <SortIcon column="product_type" /></span>
                  </TableHead>
                  <TableHead 
                    className="text-[#1a1916] font-bold text-xs uppercase text-right cursor-pointer hover:bg-[#d0c4b0]/50 select-none"
                    onClick={() => handleCostSort('quantity')}
                  >
                    <span className="flex items-center justify-end">Miktar <SortIcon column="quantity" /></span>
                  </TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Birim</TableHead>
                  <TableHead 
                    className="text-[#1a1916] font-bold text-xs uppercase text-right cursor-pointer hover:bg-[#d0c4b0]/50 select-none"
                    onClick={() => handleCostSort('unit_cost')}
                  >
                    <span className="flex items-center justify-end">Birim Fiyat <SortIcon column="unit_cost" /></span>
                  </TableHead>
                  <TableHead 
                    className="text-[#1a1916] font-bold text-xs uppercase text-right cursor-pointer hover:bg-[#d0c4b0]/50 select-none"
                    onClick={() => handleCostSort('total_cost')}
                  >
                    <span className="flex items-center justify-end">Toplam <SortIcon column="total_cost" /></span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {costRows.map((satir) => {
                  // ── Taze alt reçete: katlanabilir başlık, malzemeleri toplama DAHİL
                  if (satir.tip === 'grup') {
                    const acik = acikGruplar[satir.anahtar];
                    const pay = cost.total_cost ? (satir.grup.toplam / cost.total_cost * 100) : 0;
                    return (
                      <Fragment key={satir.anahtar}>
                        <TableRow className="border-[#d0c4b0] bg-[#e8dfd0]/50 cursor-pointer hover:bg-[#e8dfd0]"
                          onClick={() => grupCevir(satir.anahtar)}
                          data-testid={`bom-grup-${satir.anahtar}`}>
                          <TableCell className="font-bold text-[#1a1916]">
                            <span className="flex items-center gap-1.5">
                              {acik ? <ChevronAcik size={14} /> : <ChevronRight size={14} />}
                              <Layers size={13} className="text-[#8a7e70]" />
                              {satir.grup.ad}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-xs px-2 py-0.5 rounded bg-[#3d6e4e]/15 text-[#2e5a3e] font-semibold">
                              alt reçete · taze
                            </span>
                          </TableCell>
                          <TableCell colSpan={2} className="text-xs text-[#8a7e70]">
                            {satir.grup.kalemler.length} malzeme · toplamın %{trSayi(pay, 0)}'i
                          </TableCell>
                          <TableCell />
                          <TableCell className="text-right font-mono font-bold text-[#1a1916]">
                            {trPara(satir.grup.toplam)} TL
                          </TableCell>
                        </TableRow>
                        {acik && satir.grup.kalemler.map((ing, j) => (
                          <TableRow key={j} className="border-[#d0c4b0] bg-[#faf7f2]">
                            <TableCell className="text-[#3F3E39] pl-10">↳ {ing.product_name || '-'}</TableCell>
                            <TableCell>
                              <span className="text-xs px-2 py-0.5 rounded bg-[#e8dfd0] text-[#3F3E39]">
                                {typeLabels[ing.product_type] || ing.product_type || '-'}
                              </span>
                            </TableCell>
                            <TableCell className="text-right text-[#3F3E39]">{trSayi(ing.quantity, 4)}</TableCell>
                            <TableCell className="text-[#8a7e70]">{ing.unit}</TableCell>
                            <TableCell className="text-right font-mono text-[#3F3E39]">{trPara(ing.unit_cost)} TL</TableCell>
                            <TableCell className="text-right font-mono text-[#3F3E39]">{trPara(ing.total_cost)} TL</TableCell>
                          </TableRow>
                        ))}
                      </Fragment>
                    );
                  }

                  // ── Normal kalem. Ön hazırlıklı yarı mamul ise açılabilir:
                  //    içeriği BİLGİ AMAÇLI görünür, toplama dahil DEĞİL —
                  //    stoktan yarı mamulün kendisi düşüyor.
                  const ing = satir.ing;
                  const onHaz = ing.uretim_turu === 'on_hazirlikli';
                  const acik = acikGruplar[satir.anahtar];
                  return (
                    <Fragment key={satir.anahtar}>
                      <TableRow className={`border-[#d0c4b0] ${onHaz ? 'cursor-pointer hover:bg-[#e8dfd0]/40' : ''}`}
                        onClick={onHaz ? () => grupCevir(satir.anahtar) : undefined}
                        data-testid={onHaz ? `bom-onhaz-${ing.product_card_id}` : undefined}>
                        <TableCell className="font-medium text-[#1a1916]">
                          <span className="flex items-center gap-1.5">
                            {onHaz && (acik ? <ChevronAcik size={14} /> : <ChevronRight size={14} />)}
                            {ing.product_name || '-'}
                          </span>
                        </TableCell>
                        <TableCell>
                          {onHaz ? (
                            <span className="text-xs px-2 py-0.5 rounded bg-[#7a6a2e]/15 text-[#6a5a1e] font-semibold"
                              title="Bu yarı mamul önden üretilip stoklanıyor; satışta hammaddeleri değil kendisi düşer">
                              ön hazırlıklı · stoktan düşer
                            </span>
                          ) : (
                            <span className="text-xs px-2 py-0.5 rounded bg-[#e8dfd0] text-[#3F3E39]">
                              {typeLabels[ing.product_type] || ing.product_type || '-'}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-semibold text-[#1a1916]">{trSayi(ing.quantity, 4)}</TableCell>
                        <TableCell className="text-[#8a7e70]">{ing.unit}</TableCell>
                        <TableCell className="text-right font-mono text-[#3F3E39]">
                          {trPara(ing.unit_cost)} TL
                          {onHaz && ing.maliyet_kaynagi === 'recete' && (
                            <span className="block text-[10px] text-[#8a7e70]">kendi reçetesinden</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono font-semibold text-[#1a1916]">{trPara(ing.total_cost)} TL</TableCell>
                      </TableRow>
                      {onHaz && acik && (ing.alt_kalemler || []).map((alt, j) => (
                        <TableRow key={j} className="border-[#d0c4b0] bg-[#f5f0e8]/60">
                          <TableCell className="text-[#8a7e70] pl-10 italic">↳ {alt.product_name || '-'}</TableCell>
                          <TableCell colSpan={3} className="text-xs text-[#8a7e70]">
                            {trSayi(alt.quantity, 4)} {alt.unit}
                          </TableCell>
                          <TableCell colSpan={2} className="text-right font-mono text-xs text-[#8a7e70]">
                            {trPara(alt.total_cost)} TL · toplama dahil değil
                          </TableCell>
                        </TableRow>
                      ))}
                    </Fragment>
                  );
                })}
                <TableRow className="bg-[#2e2d29] hover:bg-[#2e2d29]">
                  <TableCell colSpan={5} className="text-right font-bold text-[#f5f0e8]">TOPLAM MALİYET</TableCell>
                  <TableCell className="text-right font-mono font-bold text-[#f5f0e8]">{trPara(cost.total_cost)} TL</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Lightbox */}
      <Dialog open={!!lightbox} onOpenChange={() => setLightbox(null)}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-3xl p-2">
          {lightbox && (
            <div>
              <img src={lightbox.url} alt={lightbox.caption || ''} className="w-full rounded-lg" />
              {lightbox.caption && <p className="text-center text-sm text-[#3F3E39] mt-2 pb-2">{lightbox.caption}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Product Image Lightbox */}
      <Dialog open={productImageLightbox} onOpenChange={() => setProductImageLightbox(false)}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-2xl p-2">
          {recipe?.product_image_url && (
            <div>
              <img 
                src={recipe.product_image_url} 
                alt={recipe.product_name} 
                className="w-full rounded-lg"
              />
              <p className="text-center text-lg font-semibold text-[#1a1916] mt-3 pb-2">{recipe.product_name}</p>
              <p className="text-center text-sm text-[#8a7e70] pb-2">{recipe.product_code}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoCard({ label, value, className = '' }) {
  return (
    <Card className="bg-[#faf7f2] border-[#d0c4b0]">
      <CardContent className="p-4">
        <p className="text-xs font-bold uppercase tracking-wider text-[#8a7e70] mb-1">{label}</p>
        <p className={`text-lg font-semibold text-[#1a1916] ${className}`}>{value}</p>
      </CardContent>
    </Card>
  );
}
